import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Plot from 'react-plotly.js';

// ---- Theme helpers (matching AnalyseView.tsx pattern) ----

const getThemeColors = () => {
  const isDark = document.body.classList.contains('theme-dark');
  return isDark ? {
    plotBg: 'rgba(24,24,24,0.6)',
    fontColor: '#b3b3b3',
    gridColor: 'rgba(255,255,255,0.06)',
    zeroLineColor: 'rgba(255,255,255,0.1)',
    tickColor: '#888',
    hoverBg: '#282828',
    hoverBorder: '#444',
    hoverFont: '#fff',
  } : {
    plotBg: 'rgba(255,255,255,0.9)',
    fontColor: '#374151',
    gridColor: 'rgba(0,0,0,0.06)',
    zeroLineColor: 'rgba(0,0,0,0.1)',
    tickColor: '#6b7280',
    hoverBg: '#ffffff',
    hoverBorder: '#d1d5db',
    hoverFont: '#111827',
  };
};

const makeLayout = (overrides: any = {}): any => {
  const t = getThemeColors();
  return {
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: t.plotBg,
    font: { family: "'Inter', -apple-system, sans-serif", color: t.fontColor, size: 11 },
    margin: { t: 24, r: 16, b: 48, l: 54 },
    xaxis: {
      gridcolor: t.gridColor,
      zerolinecolor: t.zeroLineColor,
      tickfont: { size: 10, color: t.tickColor },
      ...overrides.xaxis,
    },
    yaxis: {
      gridcolor: t.gridColor,
      zerolinecolor: t.zeroLineColor,
      tickfont: { size: 10, color: t.tickColor },
      ...overrides.yaxis,
    },
    hoverlabel: {
      bgcolor: t.hoverBg,
      bordercolor: t.hoverBorder,
      font: { family: "'Inter', sans-serif", color: t.hoverFont, size: 12 },
    },
    showlegend: overrides.showlegend ?? false,
    autosize: true,
    ...overrides,
  };
};

const PLOTLY_CONFIG: any = {
  displayModeBar: false,
  responsive: true,
  scrollZoom: true,
};

// ---- Types ----

export type GraphType = 'bar' | 'box' | 'scatter' | 'histogram' | 'line' | 'none';

export interface GraphConfig {
  type: GraphType;
  traces: any[];
  layoutOverrides?: any;
  annotations?: any[];
  shapes?: any[];
  title?: string;
}

interface SpectraGraphProps {
  config: GraphConfig | null;
}

const SpectraGraph: React.FC<SpectraGraphProps> = ({ config }) => {
  const plotRef = useRef<any>(null);

  // Track theme changes
  const [, setIsDark] = useState(() => document.body.classList.contains('theme-dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.body.classList.contains('theme-dark'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const handleExportPNG = useCallback(() => {
    const plotEl = document.querySelector('.spectra-plot .js-plotly-plot') as any;
    if (plotEl && (window as any).Plotly) {
      (window as any).Plotly.downloadImage(plotEl, {
        format: 'png',
        width: 1200,
        height: 800,
        scale: 2.5, // 300 DPI at ~4.8" width
        filename: 'spectra-graph',
      });
    }
  }, []);

  const handleExportSVG = useCallback(() => {
    const plotEl = document.querySelector('.spectra-plot .js-plotly-plot') as any;
    if (plotEl && (window as any).Plotly) {
      (window as any).Plotly.downloadImage(plotEl, {
        format: 'svg',
        width: 1200,
        height: 800,
        filename: 'spectra-graph',
      });
    }
  }, []);

  const handleCopyToClipboard = useCallback(async () => {
    const plotEl = document.querySelector('.spectra-plot .js-plotly-plot') as any;
    if (plotEl && (window as any).Plotly) {
      try {
        const dataUrl = await (window as any).Plotly.toImage(plotEl, {
          format: 'png',
          width: 1200,
          height: 800,
          scale: 2.5,
        });
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      } catch (err) {
        console.warn('Copy to clipboard failed:', err);
      }
    }
  }, []);

  const layout = useMemo(() => {
    if (!config) return makeLayout();
    return makeLayout({
      ...config.layoutOverrides,
      annotations: config.annotations || config.layoutOverrides?.annotations,
      shapes: config.shapes || config.layoutOverrides?.shapes,
      title: config.title ? { text: config.title, font: { size: 13 }, x: 0.5, xanchor: 'center' } : undefined,
    });
  }, [config]);

  if (!config || config.type === 'none') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'var(--text-secondary)',
        gap: '8px',
      }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '40px', height: '40px', opacity: 0.3 }}>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
        <div style={{ fontSize: '12px', opacity: 0.7 }}>
          Run an analysis to generate a graph
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Export toolbar */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '4px 8px',
        borderBottom: '1px solid var(--divider)',
        background: 'var(--hover-bg)',
      }}>
        <ExportButton label="PNG" onClick={handleExportPNG} />
        <ExportButton label="SVG" onClick={handleExportSVG} />
        <ExportButton label="Copy" onClick={handleCopyToClipboard} />
      </div>

      {/* Plot */}
      <div className="spectra-plot" style={{ flex: 1, minHeight: 0 }}>
        <Plot
          ref={plotRef}
          data={config.traces}
          layout={layout}
          config={PLOTLY_CONFIG}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
        />
      </div>
    </div>
  );
};

const ExportButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '3px 8px',
      fontSize: '10px',
      fontWeight: 500,
      borderRadius: '3px',
      border: '1px solid var(--card-border)',
      background: 'var(--card-bg)',
      color: 'var(--text-secondary)',
      cursor: 'pointer',
      transition: 'all 0.1s ease',
    }}
  >
    {label}
  </button>
);

export default SpectraGraph;

// ---- Graph builders ----

export function buildGroupComparisonGraph(
  result: any,
  headers: string[],
  groups: number[][],
  testType: string
): GraphConfig {
  const groupNames = headers.length >= groups.length
    ? headers.slice(0, groups.length)
    : groups.map((_, i) => `Group ${i + 1}`);

  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
  const useBox = testType === 'mann_whitney' || groups.some(g => g.length > 5);

  const traces: any[] = useBox
    ? groups.map((g, i) => ({
        type: 'box' as const,
        y: g,
        name: groupNames[i],
        marker: { color: colors[i % colors.length] },
        boxpoints: 'all',
        jitter: 0.3,
        pointpos: -1.8,
      }))
    : groups.map((g, i) => ({
        type: 'bar' as const,
        x: [groupNames[i]],
        y: [result.group_stats?.[i]?.mean ?? 0],
        error_y: { type: 'data', array: [result.group_stats?.[i]?.sem ?? 0], visible: true },
        name: groupNames[i],
        marker: { color: colors[i % colors.length] },
      }));

  // Significance brackets for post-hoc
  const annotations: any[] = [];
  const shapes: any[] = [];
  if (result.post_hoc && result.post_hoc.length > 0) {
    const yMax = Math.max(...groups.flat());
    const yStep = (yMax - Math.min(...groups.flat())) * 0.12;

    result.post_hoc.forEach((ph: any, idx: number) => {
      if (!ph.significant) return;
      const bracketY = yMax + yStep * (idx + 1);
      shapes.push({
        type: 'line',
        x0: ph.group_a,
        x1: ph.group_b,
        y0: bracketY,
        y1: bracketY,
        line: { color: 'var(--text-primary)', width: 1.5 },
      });
      annotations.push({
        x: (ph.group_a + ph.group_b) / 2,
        y: bracketY + yStep * 0.3,
        text: ph.label,
        showarrow: false,
        font: { size: 12, color: 'var(--text-primary)' },
      });
    });
  } else if (result.p_value != null && groups.length === 2) {
    // Simple bracket for 2-group tests
    const yMax = Math.max(...groups.flat());
    const yStep = (yMax - Math.min(...groups.flat())) * 0.12;
    const label = result.p_value < 0.001 ? '***' : result.p_value < 0.01 ? '**' : result.p_value < 0.05 ? '*' : 'ns';
    shapes.push({
      type: 'line',
      x0: 0,
      x1: 1,
      y0: yMax + yStep,
      y1: yMax + yStep,
      line: { color: 'var(--text-primary)', width: 1.5 },
    });
    annotations.push({
      x: 0.5,
      y: yMax + yStep * 1.5,
      text: label,
      showarrow: false,
      font: { size: 14, color: 'var(--text-primary)' },
    });
  }

  return {
    type: useBox ? 'box' : 'bar',
    traces,
    annotations,
    shapes,
    layoutOverrides: {
      xaxis: { title: { text: 'Group', font: { size: 11 } } },
      yaxis: { title: { text: 'Value', font: { size: 11 } } },
    },
    title: result.test_name,
  };
}

export function buildCorrelationGraph(
  result: any,
  x: number[],
  y: number[],
  headers: string[]
): GraphConfig {
  const traces: any[] = [
    {
      type: 'scatter' as const,
      mode: 'markers',
      x,
      y,
      marker: { color: '#6366f1', size: 8, opacity: 0.8 },
      name: 'Data',
    },
  ];

  // Add regression line if available
  if (result.slope != null && result.intercept != null) {
    const xMin = Math.min(...x);
    const xMax = Math.max(...x);
    const xLine = [xMin, xMax];
    const yLine = xLine.map(xi => result.slope * xi + result.intercept);
    traces.push({
      type: 'scatter' as const,
      mode: 'lines',
      x: xLine,
      y: yLine,
      line: { color: '#ec4899', width: 2, dash: 'dash' },
      name: `y = ${result.slope.toFixed(3)}x + ${result.intercept.toFixed(3)}`,
    });
  }

  const rLabel = result.r_value != null ? `r = ${result.r_value.toFixed(3)}` : '';
  const pLabel = result.p_value != null ? `p = ${result.p_value.toFixed(4)}` : '';

  return {
    type: 'scatter',
    traces,
    layoutOverrides: {
      showlegend: true,
      legend: { x: 0.02, y: 0.98, font: { size: 10 } },
      xaxis: { title: { text: headers[0] || 'X', font: { size: 11 } } },
      yaxis: { title: { text: headers[1] || 'Y', font: { size: 11 } } },
    },
    annotations: rLabel ? [{
      x: 0.98,
      y: 0.02,
      xref: 'paper',
      yref: 'paper',
      text: `${rLabel}, ${pLabel}`,
      showarrow: false,
      font: { size: 11, color: '#6366f1' },
      xanchor: 'right',
    }] : [],
    title: result.test_name,
  };
}

export function buildDescriptiveGraph(
  result: any,
  data: number[]
): GraphConfig {
  const traces: any[] = [
    {
      type: 'histogram' as const,
      x: data,
      marker: { color: 'rgba(99, 102, 241, 0.6)', line: { color: '#6366f1', width: 1 } },
      name: 'Distribution',
      nbinsx: Math.min(20, Math.ceil(Math.sqrt(data.length))),
    },
  ];

  return {
    type: 'histogram',
    traces,
    layoutOverrides: {
      xaxis: { title: { text: 'Value', font: { size: 11 } } },
      yaxis: { title: { text: 'Count', font: { size: 11 } } },
      bargap: 0.05,
    },
    title: 'Distribution',
  };
}
